import React, { useState, useEffect } from 'react';
import { TrendingUp, Flame, Info } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Player avatar with fallback
const PlayerAvatar = ({ player }) => {
  const [imgError, setImgError] = useState(false);
  
  if (player.photo_url && !imgError) {
    return (
      <img 
        src={player.photo_url}
        alt={player.name}
        className="w-12 h-12 rounded-full object-cover"
        onError={() => setImgError(true)}
      />
    );
  }
  
  // Fallback to school logo
  if (player.school_logo) {
    return (
      <div className="w-12 h-12 rounded-full bg-slate-700 flex items-center justify-center">
        <img 
          src={player.school_logo}
          alt={player.school}
          className="w-8 h-8 object-contain"
        />
      </div>
    );
  }
  
  // Final fallback: initials
  return (
    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center">
      <span className="text-white font-bold text-sm">
        {player.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
      </span>
    </div>
  );
};

// Single player card
const PlayerCard = ({ player, index }) => (
  <div 
    className="bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20 hover:bg-white/15 transition-all"
    data-testid={`player-to-watch-${index}`}
  >
    <div className="flex items-center gap-3">
      <div className="flex-shrink-0">
        <PlayerAvatar player={player} />
      </div>
      
      <div className="flex-1 min-w-0">
        <h4 className="text-white font-bold text-sm truncate">{player.name}</h4>
        <p className="text-white/60 text-xs">
          {player.position} • {player.school_abbrev || player.school}
        </p>
        {player.note && (
          <p className="text-blue-400 text-xs mt-1 line-clamp-1">{player.note}</p>
        )}
        {player.source && (
          <p className="text-white/40 text-[10px] mt-0.5">{player.source}</p>
        )}
      </div>
      
      <Flame className="w-5 h-5 text-orange-500 flex-shrink-0" />
    </div>
  </div>
);

// Main component
const PlayersToWatch = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchPlayers = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/swac-awards/players-to-watch`);
        if (!response.ok) throw new Error('Failed to fetch');
        const result = await response.json();
        setData(result);
      } catch (err) {
        console.error('Error fetching players to watch:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPlayers();
  }, []);
  
  if (loading) {
    return (
      <div data-testid="players-to-watch-loading">
        <div className="animate-pulse space-y-3">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-16 bg-white/10 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }
  
  // If no API data, show empty state with editorial label
  if (!data?.players?.length) {
    return (
      <div data-testid="players-to-watch-empty">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-6 h-6 text-orange-400" />
          <h2 className="text-xl font-black text-white uppercase">SheLovesThee Players to Watch</h2>
        </div>
        <div className="bg-white/5 rounded-lg p-4 border border-white/10">
          <div className="flex items-start gap-2 text-white/50 text-sm">
            <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>Editorial selections coming soon. Check back for our picks!</span>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div data-testid="players-to-watch">
      {/* Header with editorial label */}
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp className="w-6 h-6 text-orange-400" />
        <h2 className="text-xl font-black text-white uppercase">{data.label}</h2>
      </div>
      
      {/* Disclaimer */}
      {data.disclaimer && (
        <p className="text-white/40 text-[10px] mb-3 italic">{data.disclaimer}</p>
      )}
      
      {/* Players list */}
      <div className="space-y-3">
        {data.players.map((player, idx) => (
          <PlayerCard key={`${player.name}-${idx}`} player={player} index={idx} />
        ))}
      </div>
    </div>
  );
};

export default PlayersToWatch;
